<template>
  <button
    class="xedit sc-button"
    data-type="comments"
    @click="toggleCommentsModal"
  >
    <span>Comments ({{ count }})</span>
  </button>

  <div
    v-if="commentsModalActive"
    class="xmodal-wrapper"
    id="comments"
    :aria-expanded="expanded"
  >
    <div class="xmodal-inner">
      <div class="xmodal">
        <div class="xmodal-close">
          <span
            @click="expanded = !expanded"
            class="icon-up-right-and-down-left-from-center"
          ></span>
          <span @click="closeCommentsModal" class="icon-x"></span>
        </div>
        <div class="xmodal-body">
          <div class="xmodal-section comment-form-area">
            <h2>Comment:</h2>
            <div class="comment-form-input">
              <textarea
                name="comment"
                placeholder="Write a comment..."
                v-model="comment"
                maxlength="4000"
              />
              <button
                type="submit"
                class="sc-button submit"
                :class="{ loading: savingChanges }"
                :disabled="(comment || '').trim().length === 0 || savingChanges"
                @click="onSubmit"
              >
                <span class="button-text">Submit</span>
              </button>
            </div>
          </div>

          <div class="xmodal-section comments-area">
            <h2>Comments ({{ commentData.length }}):</h2>
            <div class="list-wrapper">
              <ul class="comments">
                <li
                  class="comment"
                  v-for="comment in commentData"
                  :key="comment.id"
                >
                  <p class="title">{{ comment.userDisplayName }}</p>
                  <p class="body">{{ comment.comment }}</p>
                  <p class="small">{{ comment.createdAtFormatted }}</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { CommentModel } from "@/types/comments";
import { CertificationVM } from "@/types/certifications";
import functions from "@/use/functions";
import { $api } from "@/services/api1";

const props = defineProps<{
  certification: CertificationVM;
}>();

const emit = defineEmits<{
  (e: "commentAdded"): void;
}>();

const certificationVM = computed<CertificationVM>(() => props.certification);

const commentsModalActive = ref<boolean>(false);

const commentData = computed<CommentModel[]>(
  () => props.certification.comments || []
);
const comments = computed<CommentModel[]>(
  () => props.certification.comments || []
);

const count = computed<number>(() => props.certification.comments.length || 0);

const comment = ref<string | null | undefined>("");

const savingChanges = ref<boolean>(false);

const expanded = ref<boolean>(false);

const isSubmitting = ref<boolean>(false);

function toggleCommentsModal() {
  commentsModalActive.value = true;
}

function closeCommentsModal() {
  commentsModalActive.value = false;
}

async function onSubmit() {
  savingChanges.value = true;

  await addComment(comment.value);
}

function resetComment() {
  comment.value = "";
}

async function addComment(comment?: string | null) {
  isSubmitting.value = true;
  const response = await $api.certifications.addComment({
    certificationId: certificationVM.value.form.id,
    appId: certificationVM.value.appId,
    loanId: certificationVM.value.loanId,
    comment: comment,
  });

  if (response === true) {
    resetComment();
    isSubmitting.value = false;
    functions.openModal("Your comment has been added!");
    emit("commentAdded");

    return true;
  } else {
    functions.openModal("Error: Comment was not saved!");
    isSubmitting.value = false;
    return false;
  }
}

watch(isSubmitting, (newValue, oldValue) => {
  savingChanges.value = newValue;
});
</script>

<style scoped>
.sc-button[data-type="comments"] {
  float: right;
  cursor: pointer;
  display: flex;
  width: 136px;
  min-height: 28px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 4px;
  flex-shrink: 0;
  padding: 0;
  font-size: 14px;
  max-height: 40px;
  gap: 2px;
}

.sc-button,
.sc-button-text {
  color: #fff;
  font-family: "Montserrat";
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 142.857% */
  padding: 11px 10px;
}

div.xmodal-inner {
  width: 400px;
}
.xmodal {
  padding: 0;
}

h2 {
  color: #000;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  margin-bottom: 0;
}

textarea {
  resize: none;
  position: relative;
  outline: none;
  padding: 6px 13px 19px 10px;
  height: 100%;
  width: 100%;
}

textarea::placeholder {
  color: #000;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 114.286% */
}

div.comment-form-area {
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
  padding: 10px 15px;
}

.comment-form-input {
  display: flex;
  position: relative;
  overflow-y: auto;
  align-items: flex-end;
  width: 370px;
  height: 100px;
  margin-top: 9px;
}

.comment-form-input button.submit {
  position: absolute;
  bottom: 6px;
  right: 6px;
  color: #fff;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 142.857% */
}

button[type="submit"] {
  padding: 4px 16px 4px 17px;
  justify-content: center;
  align-items: center;
  float: right;
  position: absolute;
  border-radius: 4px;
}

button[type="submit"]:disabled {
  opacity: 0.7;
  background: #c3c3c3;
  border-color: transparent;
  cursor: not-allowed;
}

button[type="submit"]:is(.loading) {
  cursor: wait;
}

button[type="submit"]:disabled:is(.loading)::after {
  content: "";
  position: absolute;
  width: 16px;
  height: 16px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 4px solid transparent;
  border-top-color: #f7f7f8;
  border-radius: 50%;
  animation: button-loading-spinner 1s ease infinite;
}

button[type="submit"]:is(.loading) .button-text {
  visibility: hidden;
  opacity: 0;
}

.xmodal-close {
  padding-top: 12px;
  padding-right: 17px;
  display: flex;
  gap: 0.5em;
  position: absolute;
  right: 0;
  top: 0;
}

.comments-wrapper {
  padding-top: 22px;
  border-top: 1px solid #ced1d9;
}

.sc-comment {
  padding: 10px 15px;
}

.list-wrapper {
  overflow-y: auto;
  max-height: 50vh;
  overflow-x: hidden;
}
/* Style list */
ul {
  padding: 0;
  margin-top: 0;
  list-style-type: none;
}

ul li {
  padding: 8px 15px 2px;
  margin: 0;
}
ul li:nth-child(odd) {
  background-color: #f2f3f5; /* Light gray for odd items */
}

ul li:nth-child(even) {
  background-color: #fff; /* White for even items */
}

.comment p {
  color: #000;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  margin: 2px;
}
.comment p.title {
  color: #000;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}
.comment p.small {
  color: #8e908e;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 200% */
}

.comments-area h2 {
  margin-bottom: 5px;
}

.xmodal-section {
  display: flex;
  flex-direction: column;
}

.comments-area h2 {
  padding-left: 15px;
}

.xmodal-wrapper[aria-expanded="true"] .xmodal-inner {
  width: 100%;
}

.xmodal-wrapper[aria-expanded="true"] .comment-form-input {
  width: 100%;
}

@media screen and (max-width: 620px) {
  .xmodal-inner {
    width: 100%;
  }
}

@keyframes button-loading-spinner {
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
</style>
